import React, { Component } from 'react';
import html2pdf from 'html2pdf.js';
import {
  Truck,
  X,
  Upload,
  Clock,
  Package,
  Search,
  ArrowBigLeft,
  Download,
  Printer,
  CheckCircle,
} from 'lucide-react';
import '../../../../../styles/components/vehicle-register.scss';
import { toast } from 'react-toastify';
import i18n from '../../../../../i18n';
import {
  createWeighbridgeTicket,
  exitWeighbridgeTicket,
  getDeliverySitesList,
  getVehiclesForSite,
  getWeighbridgeTickets,
} from '../services/MaterialLoadingService';
import WeightTicketPrint from './WeightTicketPrint';

class MaterialLoading extends Component {
  constructor(props) {
    super(props);
    this.printRef = React.createRef();
    this.state = {
      showPrintModal: false,
      printTicketData: null,
      type: 'non-opal',
      yardId: '',
      date: '',
      auctionSite: '',
      gatepassNumber: '',
      weightTicket: '',
      vehicleId: '',
      sites: [],
      vehicles: [],
      loadingSites: false,
      loadingVehicles: false,
      hideSidebar: false,
      status: 'IN',
      errors: {},
      isEditing: false,
      currentTime: new Date().toISOString(),

      // Ticket Basic Information
      ticketId: 'TKT-' + Date.now().toString().slice(-6),
      operationType: 'check-in',
      ticketDate: new Date().toISOString().split('T')[0],
      ticketTime: new Date().toTimeString().slice(0, 5),
      ticketStatus: 'loading',
      checklist: {
        safetyChecked: false,
        VehicleChecked: false,
      },

      // Vehicle Information
      vehicleNumber: '',
      trailerNumber: '',
      vehicleType: '',
      vehicleModel: '',
      vehicleCapacity: '',

      // Driver Information
      driverName: '',
      driverContact: '',
      driverLicense: '',

      tareWeight: '',
      grossWeight: '',
      netWeight: '',
      weighbridgeLocation: '',

      delivery_site: {},
      // Location Information
      loadingPoint: '',
      destination: '',
      yardLocation: '',
      facility: '',

      // Material Information
      materialType: '',
      auctionLot: '',
      materialQuantity: '',

      // Documentation
      waybillNumber: '',
      bosNumber: '',
      supervisorName: '',

      // Safety & Compliance
      safetyStatus: 'pending',
      technicalClearance: false,
      financialClearance: false,
      inspectionNotes: '',

      // Timing Information
      actualArrival: '',
      actualDeparture: '',
      processingDuration: '',
      vehiclePhoto: '',

      vehiclePhotos: [],
      recentTickets: [],
      searchQuery: '',
      yard: {},

      // Auto-download states
      isGeneratingPDF: false,
      autoDownloadAfterExit: true,
    };
  }

  componentDidMount() {
    this.timeInterval = setInterval(() => {
      this.setState({ currentTime: new Date().toISOString() });
    }, 60000);

    const today = new Date().toISOString().split('T')[0];
    this.setState({ date: today });
    this.fetchSitesList();
    this.fetchWeighbridgeTickets();
  }

  componentWillUnmount() {
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
    }
  }

  calculateProcessingDuration = () => {
    const { actualArrival, actualDeparture, currentTime } = this.state;
    const endTime = actualDeparture || currentTime;

    if (!actualArrival || !endTime) {
      return 'Not available';
    }

    try {
      const arrival = new Date(actualArrival);
      const departure = new Date(endTime);

      if (isNaN(arrival.getTime()) || isNaN(departure.getTime())) {
        return 'Invalid time format';
      }

      const durationMs = departure - arrival;

      if (durationMs < 0) {
        return 'Invalid time range';
      }

      const durationMinutes = Math.round(durationMs / (1000 * 60));
      const days = Math.floor(durationMinutes / (60 * 24));
      const hours = Math.floor((durationMinutes % (60 * 24)) / 60);
      const minutes = durationMinutes % 60;

      let result = '';

      if (days > 0) {
        result = `${days} day${days > 1 ? 's' : ''}`;
        if (hours > 0) {
          result += `, ${hours} hour${hours > 1 ? 's' : ''}`;
        }
        if (minutes > 0 && days < 3) {
          result += `, ${minutes} minute${minutes > 1 ? 's' : ''}`;
        }
      } else if (hours > 0) {
        result = `${hours} hour${hours > 1 ? 's' : ''}`;
        if (minutes > 0) {
          result += `, ${minutes} minute${minutes > 1 ? 's' : ''}`;
        }
      } else {
        result = `${minutes} minute${minutes > 1 ? 's' : ''}`;
      }

      if (!actualDeparture) {
        result += ' (ongoing)';
      }

      return result;
    } catch (error) {
      console.error('Error calculating duration:', error);
      return 'Calculation error';
    }
  };

  handlePrintTicket = async (ticket, autoDownload = false) => {
    // Get material name from the ticket
    const materialName =
      ticket.delivery_site?.material?.name || ticket.materialType || 'No Material Specified';

    const printData = {
      ...ticket,
      gatepass_number: ticket.gatepass_number || this.state.gatepassNumber,
      weight_ticket: ticket.weight_ticket || this.state.weightTicket,
      tare_weight: parseFloat(ticket.tare_weight || this.state.tareWeight || 0),
      gross_weight: parseFloat(ticket.gross_weight || this.state.grossWeight || 0),
      net_weight: parseFloat(ticket.net_weight || this.state.netWeight || 0),
      entry_time: ticket.entry_time || ticket.checkInTime || this.state.actualArrival,
      exit_time: ticket.exit_time || ticket.checkOutTime || this.state.actualDeparture,
      ticket_number: ticket.ticket_number || this.state.ticketId,
      // Add yard information
      yard: ticket.yard || {
        // <-- ADD THIS
        id: this.state.yardId,
        name: this.state.yard?.name || ticket.yard?.name || 'Not Specified',
      },
      // Add material name directly to printData for easier access
      materialName: materialName,
      // Ensure delivery_site has material data
      delivery_site: {
        ...ticket.delivery_site,
        material: ticket.delivery_site?.material || {
          name: materialName,
        },
      },
    };

    this.setState(
      {
        printTicketData: printData,
        printMaterialName: materialName,
        isGeneratingPDF: true,
      },
      async () => {
        await new Promise((resolve) => setTimeout(resolve, 300));

        try {
          if (!this.printRef.current) {
            throw new Error('Print component not ready. Please try again.');
          }

          await this.generateAndDownloadPDF(autoDownload);

          if (!autoDownload) {
            this.setState({ showPrintModal: true });
          }
        } catch (error) {
          console.error('Error generating PDF:', error);
          toast.error(error.message || 'Failed to generate PDF');
        } finally {
          this.setState({ isGeneratingPDF: false });
        }
      }
    );
  };

  handleTicketClick = (ticket) => {
    const isCompleted =
      ticket.status === 'OUT' || (ticket.status === 'completed' && ticket.exit_time);
    let ticketStatus = ticket.status;
    const yards = ticket.delivery_site?.yards || [];
    const yardCount = yards.length;

    if (ticket.status === 'loading' && ticket.exit_time) {
      ticketStatus = 'completed';
    }

    // Process vehicle photos from API response
    const processedVehiclePhotos = this.processVehiclePhotosFromAPI(ticket);

    // Get the previously selected yard for this ticket
    const previousYard = ticket.yard_id || '';

    this.setState({
      isEditing: true,
      isCompleted: isCompleted,
      ticketId: ticket.ticketNumber,
      operationType: ticket.status === 'IN' ? 'check-in' : 'check-out',
      ticketDate: new Date().toISOString().split('T')[0],
      ticketTime: new Date().toTimeString().slice(0, 5),
      ticketStatus: ticketStatus,

      yard: ticket.yard || {
        // <-- ADD THIS
        id: ticket.yard_id || '',
        name: ticket.yard?.name || '',
      },
      yardId: ticket.yard_id || ticket.yard?.id || '',

      currentTicketYards: yards,
      yardCount: yardCount,
      vehicleNumber: ticket.vehicleNumber,
      trailerNumber: ticket.trailerNumber,
      vehicleId: ticket.vehicle?.id || '',
      driverName: ticket.driverName,
      driverContact: ticket.driverContact || '',
      driverLicense: ticket.driverLicense || '',
      tareWeight: ticket.tareWeight || '',
      grossWeight: ticket.grossWeight || '',
      netWeight: ticket.netWeight || '',
      weighbridgeLocation: ticket.weighbridgeLocation || '',

      // Auto-populate gatepass number and weight ticket
      gatepassNumber: ticket.gatepass_number || '',
      weightTicket: ticket.weight_ticket || '',

      loadingPoint: ticket.loadingPoint || '',
      destination: ticket.destination || '',
      yardLocation: ticket.yardLocation || '',
      facility: ticket.facility || '',
      vehiclePhoto: ticket.vehiclePhoto,
      materialType: ticket.materialType || '', // This should now have the material name
      auctionLot: ticket.auctionLot || '',
      materialQuantity: ticket.materialQuantity || '',
      waybillNumber: ticket.waybillNumber || '',
      bosNumber: ticket.bosNumber || '',
      supervisorName: ticket.supervisorName || '',
      safetyStatus: ticket.safetyStatus || 'pending',
      technicalClearance: ticket.technicalClearance || false,
      financialClearance: ticket.financialClearance || false,
      inspectionNotes: ticket.inspectionNotes || '',
      actualArrival: ticket.checkInTime || '',
      actualDeparture: ticket.checkOutTime || '',
      processingDuration: ticket.processingDuration || '',

      // Auto-populate vehicle photos
      vehiclePhotos: processedVehiclePhotos,

      errors: {},
    });

    toast.success(
      `Loaded ticket ${ticket.ticketNumber} for ${isCompleted ? 'viewing' : 'editing'}`
    );
  };

  processVehiclePhotosFromAPI = (ticket) => {
    const photos = [];

    // Get the base URL for images from environment variable
    const imageBaseUrl = process.env.REACT_APP_IMAGE_URL || '';

    // Process photos from API response
    if (ticket.photos && Array.isArray(ticket.photos)) {
      ticket.photos.forEach((photo, index) => {
        if (photo.photo_path) {
          // Create a full URL by combining the base URL with the photo path
          const photoUrl = `${imageBaseUrl}${photo.photo_path}`;
          photos.push({
            id: photo.id || `api-photo-${index}`,
            url: photoUrl,
            name: `Vehicle Photo ${index + 1}`,
            source: 'api',
            file: null,
          });
        }
      });
    }

    // Also check for all_photos if available
    if (ticket.all_photos && Array.isArray(ticket.all_photos)) {
      ticket.all_photos.forEach((photo, index) => {
        if (photo.photo_path || photo.url) {
          const photoUrl = photo.photo_path ? `${imageBaseUrl}${photo.photo_path}` : photo.url;
          photos.push({
            id: photo.id || `all-photo-${index}`,
            url: photoUrl,
            name: `Vehicle Photo ${index + 1}`,
            source: 'api',
            file: null,
          });
        }
      });
    }

    return photos;
  };

  handleInputChange = (field, value) => {
    this.setState({
      [field]: value,
      errors: { ...this.state.errors, [field]: '' },
    });

    // Auto-calculate net weight when tare or gross weight changes
    if (field === 'tareWeight' || field === 'grossWeight') {
      this.calculateNetWeight();
    }
  };

  calculateNetWeight = () => {
    const { tareWeight, grossWeight } = this.state;
    if (tareWeight && grossWeight) {
      const net = parseFloat(grossWeight) - parseFloat(tareWeight);
      this.setState({
        netWeight: net > 0 ? net.toFixed(2) : '0.00',
      });
    } else {
      this.setState({ netWeight: '' });
    }
  };

  handleStatus = (status) => {
    // Don't allow status change while loading
    if (this.state.isLoading) {
      toast.warning('Please wait, operation in progress');
      return;
    }

    // Don't allow status change if editing a completed ticket
    if (this.state.isCompleted) {
      toast.warning('Cannot change status of completed ticket');
      return;
    }

    // Update the status
    this.setState({ status: status }, () => {
      // Reset form based on status change
      if (status === 'IN') {
        // Switching to IN (check-in mode)
        this.setState({
          operationType: 'check-in',
          ticketId: 'TKT-' + Date.now().toString().slice(-6),
          grossWeight: '',
          netWeight: '',
          gatepassNumber: '',
          weightTicket: '',
          actualArrival: new Date().toISOString().slice(0, 19).replace('T', ' '),
          actualDeparture: '',
          processingDuration: '',
          vehiclePhotos: [],
          isEditing: false,
          isCompleted: false,
          errors: {},
        });
        toast.info('Switched to Check-in mode - Showing active tickets');
      } else if (status === 'OUT') {
        // Switching to OUT (check-out mode)
        this.setState({
          operationType: 'check-out',
          isEditing: false,
          isCompleted: false,
          vehiclePhotos: [],
          errors: {},
        });
        toast.info('Switched to Check-out mode - Showing completed tickets');
      }

      // Refresh tickets list with current site ID and new status
      if (this.state.auctionSite) {
        this.fetchWeighbridgeTickets(this.state.auctionSite);
      } else {
        // If no site selected, still fetch tickets without site filter
        this.fetchWeighbridgeTickets();
      }
    });
  };

  handleOperationTypeChange = (operationType) => {
    const currentTime = new Date().toISOString().slice(0, 19).replace('T', ' ');

    if (operationType === 'check-in') {
      this.setState({
        actualArrival: currentTime,
        actualDeparture: '',
        processingDuration: '',
      });
    } else if (operationType === 'check-out') {
      this.setState({
        actualDeparture: currentTime,
        processingDuration: this.calculateProcessingDuration(),
      });
    }
  };

  validateForm = () => {
    const errors = {};

    // Check-in validation
    if (this.state.operationType === 'check-in') {
      if (!this.state.auctionSite) errors.auctionSite = 'Delivery site is required';
      if (!this.state.vehicleId) errors.vehicleId = 'Vehicle selection is required';
      if (!this.state.tareWeight || parseFloat(this.state.tareWeight) <= 0) {
        errors.tareWeight = 'Valid tare weight is required';
      }
    }

    // Check-out validation
    if (this.state.operationType === 'check-out') {
      if (!this.state.ticketId || this.state.ticketId.startsWith('TKT-')) {
        errors.ticketId = 'Please select a valid existing ticket to check-out';
      }
      if (!this.state.grossWeight || parseFloat(this.state.grossWeight) <= 0) {
        errors.grossWeight = 'Valid gross weight is required for check-out';
      }
      if (!this.state.gatepassNumber) errors.gatepassNumber = 'Gatepass number is required';
      if (!this.state.weightTicket) errors.weightTicket = 'Weight ticket is required';

      // Check if site has yards
      if (this.state.currentTicketYards && this.state.currentTicketYards.length > 0) {
        if (!this.state.yardId) {
          errors.yardId = 'Please select a yard for this delivery site';
        }
      }

      // Check for photos (only for new check-outs, not for viewing completed tickets)
      if (!this.state.isCompleted && this.state.vehiclePhotos.length === 0) {
        errors.photos = 'At least one photo is required for check-out';
      }
    }

    return errors;
  };

  handleTypeChange = (value) => {
    this.setState({
      type: value,
    });
  };

  handleSubmit = async () => {
    const errors = this.validateForm();

    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      toast.error('Please fix the validation errors before submitting');
      return;
    }

    try {
      this.setState({ isLoading: true });

      const ticketData = {
        vehicle_id: this.state.vehicleId,
        delivery_site_id: this.state.auctionSite,
        driver_id: null,
        tare_weight: this.state.tareWeight,
      };

      if (this.state.vehicleId) {
        const selectedVehicle = this.state.vehicles.find((v) => v.id == this.state.vehicleId);
        if (selectedVehicle?.driver?.id) {
          ticketData.driver_id = selectedVehicle.driver.id;
        }
      }

      const response = await createWeighbridgeTicket(ticketData);

      if (response.success) {
        toast.success(`Ticket ${response.data.ticket_number} created successfully!`);

        this.setState({
          showSuccessModal: true,
          createdTicket: response.data,
          ticketNumber: response.data.ticket_number,
          createdTicketId: response.data.id,
          ticketId: response.data.ticket_number,
          yardId: '',
          vehiclePhotos: [],
        });

        setTimeout(() => {
          this.setState({ showSuccessModal: false });
        }, 5000);

        // Refresh the tickets list
        this.fetchWeighbridgeTickets();
      } else {
        if (response.errors) {
          const apiErrors = {};
          Object.keys(response.errors).forEach((key) => {
            apiErrors[key] = response.errors[key][0];
          });
          this.setState({ errors: apiErrors });
          toast.error('Please check the form for errors');
        } else {
          toast.error(response.message || 'Failed to create ticket');
        }
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Failed to create ticket: ' + (error.message || 'Unknown error'));
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleExitSubmit = async () => {
    const errors = this.validateForm();

    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      toast.error('Please fix the validation errors before submitting');
      return;
    }

    try {
      this.setState({ isLoading: true });

      // Prepare FormData for exit
      const formData = new FormData();
      formData.append('gross_weight', this.state.grossWeight);

      // Add yard_id if selected
      if (this.state.yardId) {
        formData.append('yard_id', this.state.yardId);
      }

      if (this.state.gatepassNumber) {
        formData.append('gatepass_number', this.state.gatepassNumber);
      }

      if (this.state.weightTicket) {
        formData.append('weight_ticket', this.state.weightTicket);
      }

      // Get the ticket ID from the ticket number
      const ticketId = this.getTicketIdFromNumber(this.state.ticketId);

      if (!ticketId) {
        toast.error('Ticket ID not found');
        this.setState({ isLoading: false });
        return;
      }

      // Add photos (only new uploads, not from API)
      if (this.state.vehiclePhotos.length > 0) {
        this.state.vehiclePhotos.forEach((photo, index) => {
          // Only upload if it's a new file (not from API)
          if (photo.file && photo.source !== 'api') {
            formData.append(
              'photos[]',
              photo.file,
              photo.file.name || `vehicle_photo_${index}.jpg`
            );
          }
        });
      }

      const response = await exitWeighbridgeTicket(formData, true, ticketId);

      if (response.success) {
        toast.success(`Check-out recorded successfully for ticket ${this.state.ticketId}!`);

        // Prepare the updated ticket data for printing
        const updatedTicket = {
          ...response.data,
          gatepass_number: this.state.gatepassNumber,
          weight_ticket: this.state.weightTicket,
          tare_weight: parseFloat(this.state.tareWeight),
          gross_weight: parseFloat(this.state.grossWeight),
          net_weight: parseFloat(this.state.netWeight),
        };

        // Auto-download the weight ticket
        if (this.state.autoDownloadAfterExit && !this.state.isCompleted) {
          setTimeout(async () => {
            try {
              await this.handlePrintTicket(updatedTicket, true);
            } catch (error) {
              console.error('Auto-download failed:', error);
              toast.info('You can manually download the ticket using the Print button');
            }
          }, 500);
        }

        // Reset form for new entry
        this.setState({
          yardId: '',
          showSuccessModal: true,
          updatedTicket: updatedTicket,
          ticketNumber: response.data.ticket_number,
          operationType: 'check-in',
          ticketId: 'TKT-' + Date.now().toString().slice(-6),
          grossWeight: '',
          netWeight: '',
          gatepassNumber: '',
          weightTicket: '',
          vehiclePhotos: [],
          currentTicketYards: [],
          yardCount: 0,
          errors: {},
        });

        // Refresh the tickets list
        this.fetchWeighbridgeTickets();

        setTimeout(() => {
          this.setState({ showSuccessModal: false });
        }, 5000);
      } else {
        if (response.errors) {
          const apiErrors = {};
          Object.keys(response.errors).forEach((key) => {
            apiErrors[key] = response.errors[key][0];
          });
          this.setState({ errors: apiErrors });
          toast.error('Please check the form for errors');
        } else {
          toast.error(response.message || 'Failed to record exit');
        }
      }
    } catch (error) {
      console.error('Error recording exit:', error);
      toast.error('Failed to record exit: ' + (error.message || 'Unknown error'));
    } finally {
      this.setState({ isLoading: false });
    }
  };

  getTicketIdFromNumber = (ticketNumber) => {
    const ticket = this.state.recentTickets.find((t) => t.ticketNumber === ticketNumber);
    return ticket ? ticket.id : null;
  };

  handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);

    if (files.length > 5) {
      toast.error('Maximum 5 photos allowed');
      return;
    }

    if (files.length > 0) {
      const uploadedPhotos = files
        .map((file) => {
          if (!file.type.startsWith('image/')) {
            toast.error('Please upload image files only');
            return null;
          }

          if (file.size > 5 * 1024 * 1024) {
            toast.error(`File ${file.name} size should be less than 5MB`);
            return null;
          }

          const previewUrl = URL.createObjectURL(file);

          return {
            id: Date.now() + Math.random(),
            url: previewUrl,
            file: file,
            name: file.name,
            source: 'upload',
          };
        })
        .filter((photo) => photo !== null);

      if (uploadedPhotos.length > 0) {
        // Remove any existing uploaded photos (keep API photos)
        const existingPhotos = this.state.vehiclePhotos.filter((photo) => photo.source === 'api');

        this.setState({
          vehiclePhotos: [...existingPhotos, ...uploadedPhotos],
          errors: { ...this.state.errors, photos: '' },
        });

        toast.success(`${uploadedPhotos.length} photo(s) uploaded successfully`);
      }
    }
  };

  removePhoto = (id) => {
    this.setState((prevState) => {
      const photoToRemove = prevState.vehiclePhotos.find((p) => p.id === id);

      // Clean up blob URL if it's an uploaded photo
      if (photoToRemove && photoToRemove.url.startsWith('blob:')) {
        URL.revokeObjectURL(photoToRemove.url);
      }

      return {
        vehiclePhotos: prevState.vehiclePhotos.filter((photo) => photo.id !== id),
        errors: { ...prevState.errors, photos: '' },
      };
    });
  };

  resetForm = () => {
    this.setState({
      isEditing: false,
      isCompleted: false,
      ticketId: 'TKT-' + Date.now().toString().slice(-6),
      operationType: 'check-in',
      ticketDate: new Date().toISOString().split('T')[0],
      ticketTime: new Date().toTimeString().slice(0, 5),
      ticketStatus: 'loading',
      vehicleNumber: '',
      trailerNumber: '',
      vehicleId: '',
      driverName: '',
      driverContact: '',
      driverLicense: '',
      tareWeight: '',
      grossWeight: '',
      netWeight: '',
      gatepassNumber: '',
      weightTicket: '',
      loadingPoint: '',
      destination: '',
      yardId: '',
      currentTicketYards: [],
      yardCount: 0,
      vehiclePhotos: [],
      errors: {},
    });
  };

  handleSearch = (query) => {
    this.setState({ searchQuery: query });
  };

  getFilteredTickets = () => {
    const { recentTickets, searchQuery } = this.state;
    if (!searchQuery) return recentTickets.slice(0, 30);

    return recentTickets.filter(
      (ticket) =>
        ticket.vehicleNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.driverName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.ticketNumber.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  fetchSitesList = async () => {
    this.setState({ loadingSites: true });

    try {
      const filters = {};
      const response = await getDeliverySitesList(filters);

      if (response.success && response.data) {
        const sites = response.data.map((site) => ({
          id: site.value,
          value: site.value,
          label: site.label,
          name: site.label,
          load_from: site.load_from || '',
          load_to: site.load_to || '',
          data: site.data || {},
        }));

        this.setState({
          sites,
          loadingSites: false,
        });
      } else {
        toast.error(response.message || 'Failed to fetch sites');
        this.setState({ loadingSites: false });
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
      toast.error('Error loading sites list');
      this.setState({ loadingSites: false });
    }
  };

  fetchVehiclesList = async (siteId) => {
    this.setState({ loadingVehicles: true });

    try {
      const filters = {};
      const response = await getVehiclesForSite(siteId, filters);

      if (response.success && response.data) {
        let vehicles = [];

        if (Array.isArray(response.data)) {
          vehicles = response.data.map((item) => {
            const vehicle = item.vehicle || item;
            const driver = vehicle.driver || item.driver || null;

            return {
              id: vehicle.id,
              value: vehicle.id,
              label: vehicle.vehicle_number || `Vehicle ${vehicle.id}`,
              name: vehicle.vehicle_number,
              vehicle_number: vehicle.vehicle_number,
              vehicle_model: vehicle.vehicle_model,
              capacity: vehicle.capacity,
              status: vehicle.status,
              driver: driver
                ? {
                    id: driver.id,
                    name: driver.name || driver.driver_name,
                    phone: driver.phone || driver.driver_contact,
                    license_number: driver.license_number || driver.driver_license,
                  }
                : null,
            };
          });
        } else if (response.data.vehicles) {
          vehicles = response.data.vehicles.map((vehicle) => {
            const driver = vehicle.driver || null;

            return {
              id: vehicle.id,
              value: vehicle.id,
              label: vehicle.vehicle_number || `Vehicle ${vehicle.id}`,
              name: vehicle.vehicle_number,
              vehicle_number: vehicle.vehicle_number,
              vehicle_model: vehicle.vehicle_model,
              capacity: vehicle.capacity,
              status: vehicle.status,
              driver: driver
                ? {
                    id: driver.id,
                    name: driver.name,
                    phone: driver.phone || driver.phone,
                    license_number: driver.license_number,
                  }
                : null,
            };
          });
        }

        this.setState({
          vehicles,
          loadingVehicles: false,
        });
      } else {
        toast.error(response.message || 'Failed to fetch vehicles for this site');
        this.setState({ loadingVehicles: false, vehicles: [] });
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Error loading vehicles list');
      this.setState({
        loadingVehicles: false,
        vehicles: [],
        vehicleId: '',
        vehicleNumber: '',
        trailerNumber: '',
        driverName: '',
        driverContact: '',
        driverLicense: '',
      });
    }
  };

  fetchWeighbridgeTickets = async (siteId) => {
    this.setState({ isLoadingTickets: true });

    try {
      const filters = {
        status: this.state.status === 'IN' ? 'IN' : 'OUT',
      };

      // Only add site_id to filters if it's provided
      if (siteId) {
        filters.site_id = siteId;
      }

      const response = await getWeighbridgeTickets(filters);

      if (response.success && response.data && response.data.data) {
        const tickets = response.data.data.map((ticket) => {
          let status = 'loading';
          if (ticket.status === 'IN') {
            status = 'loading';
          } else if (ticket.status === 'OUT') {
            status = 'completed';
          }

          let processingDuration = '';
          if (ticket.entry_time && ticket.exit_time) {
            const entry = new Date(ticket.entry_time);
            const exit = new Date(ticket.exit_time);
            const durationMs = exit - entry;
            const minutes = Math.round(durationMs / (1000 * 60));
            processingDuration = `${minutes} minutes`;
          }

          return {
            id: ticket.id,
            ticketNumber: ticket.ticket_number,
            gatepassNumber: ticket.gatepass_number || '',
            weightTicket: ticket.weight_ticket || '',
            vehicleNumber: ticket.vehicle?.vehicle_number || 'Unknown',
            trailerNumber: ticket.vehicle?.trailer_number || 'Unknown',
            driverName: ticket.driver?.name || 'Not assigned',
            driverContact: ticket.driver?.phone || '',
            yard: ticket?.yard,
            driverLicense: ticket.driver?.license_number || '',
            operationType: ticket.status === 'IN' ? 'check-in' : 'check-out',
            materialType: ticket.delivery_site?.material?.name || '',
            auctionLot: '',
            materialQuantity: ticket.net_weight ? `${parseFloat(ticket.net_weight)}` : '',
            checkInTime: ticket.entry_time,
            checkOutTime: ticket.exit_time,
            tareWeight: ticket.tare_weight ? `${parseFloat(ticket.tare_weight)}` : '',
            grossWeight: ticket.gross_weight ? `${parseFloat(ticket.gross_weight)}` : '',
            netWeight: ticket.net_weight ? `${parseFloat(ticket.net_weight)}` : '',
            weighbridgeLocation: '',
            loadingPoint: '',
            destination: '',
            yardLocation: '',
            facility: ticket.delivery_site?.project_name || 'Unknown',
            waybillNumber: '',
            bosNumber: '',
            supervisorName: '',
            safetyStatus: 'approved',
            technicalClearance: true,
            financialClearance: true,
            inspectionNotes: ticket.remarks || '',
            status: status,
            vehiclePhotos: ticket.all_photos,
            processingDuration: processingDuration,
            delivery_site: ticket.delivery_site,
            photos: ticket.photos || [],
            gatepass_number: ticket.gatepass_number,
            weight_ticket: ticket.weight_ticket,
            all_photos: ticket.all_photos || [],
          };
        });

        this.setState({
          recentTickets: tickets,
          isLoadingTickets: false,
        });
      } else {
        if (response.message && !response.message.includes('No data')) {
          toast.error(response.message || 'Failed to fetch weighbridge tickets');
        }
        this.setState({
          recentTickets: [],
          isLoadingTickets: false,
        });
      }
    } catch (error) {
      console.error('Error fetching weighbridge tickets:', error);
      // Don't show error toast for empty results
      if (error.message && !error.message.includes('404')) {
        toast.error('Error loading weighbridge tickets');
      }
      this.setState({
        recentTickets: [],
        isLoadingTickets: false,
      });
    }
  };

  handleSitesChange = (e) => {
    const siteId = e.target.value;
    const selectedSite = this.state.sites.find((site) => site.id == siteId);

    this.setState((prevState) => ({
      auctionSite: siteId,
      errors: { ...prevState.errors, auctionSite: '' },
      vehicles: [],
      vehicleId: '',
      vehicleNumber: '',
      trailerNumber: '',
      driverName: '',
      driverContact: '',
      driverLicense: '',
      loadingPoint: selectedSite?.load_from || '',
      destination: selectedSite?.load_to || '',
      materialType: '',
      auctionLot: selectedSite?.label?.split('.')[1]?.trim() || '',
    }));

    if (siteId) {
      this.fetchVehiclesList(siteId);
      this.fetchWeighbridgeTickets(siteId);

      if (selectedSite) {
        toast.success(`Site "${selectedSite.label}" selected. Loading vehicles...`);
      }
    }
  };

  generateAndDownloadPDF = async (autoDownload = false) => {
    if (!this.printRef.current) {
      toast.error('Print reference not found');
      throw new Error('Print reference not found');
    }

    const element = this.printRef.current;
    element.style.display = 'block';
    element.style.visibility = 'visible';

    const options = {
      margin: 10,
      filename: `Weight_Ticket_${this.state.printTicketData?.weight_ticket || this.state.printTicketData?.ticket_number || this.state.ticketId}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        allowTaint: true,
        backgroundColor: '#FFFFFF',
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
      },
    };

    try {
      if (!autoDownload) {
        toast.info('Generating PDF...');
      }

      await html2pdf().set(options).from(element).save();

      if (autoDownload) {
        toast.success('Weight ticket downloaded automatically!');
      } else {
        toast.success('Weight ticket downloaded!');
      }

      return true;
    } catch (error) {
      console.error('PDF generation error:', error);

      try {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
        <html>
          <head>
            <title>Weight Ticket - ${this.state.printTicketData?.ticket_number || this.state.ticketId}</title>
            <style>
              ${document.querySelector('#weight-ticket-styles')?.innerHTML || ''}
            </style>
          </head>
          <body>
            ${element.innerHTML}
            <script>
              setTimeout(() => {
                window.print();
                setTimeout(() => window.close(), 1000);
              }, 500);
            </script>
          </body>
        </html>
      `);
        printWindow.document.close();

        toast.info('Opening print dialog...');
      } catch (printError) {
        console.error('Print fallback error:', printError);
        toast.error('Please try the print button instead');
      }

      throw error;
    }
  };

  handleVehicleChange = (e) => {
    const vehicleId = e.target.value;
    const selectedVehicle = this.state.vehicles.find((v) => v.id == vehicleId);

    this.setState({
      vehicleId: vehicleId,
      vehicleNumber: selectedVehicle?.vehicle_number || '',
      trailerNumber: selectedVehicle?.trailer_number || '',
      vehicleModel: selectedVehicle?.vehicle_model || '',
      driverName: selectedVehicle?.driver?.name || '',
      driverContact: selectedVehicle?.driver?.phone || '',
      driverLicense: selectedVehicle?.driver?.license_number || '',
      errors: { ...this.state.errors, vehicleId: '' },
    });

    if (selectedVehicle) {
      toast.success(`Vehicle ${selectedVehicle.vehicle_number} selected`);
    } else {
      toast.error('Vehicle not found in the list');
    }
  };

  handleDirectDownload = () => {
    // Get the current ticket data from state
    const currentTicket = this.state.recentTickets.find(
      (t) => t.ticketNumber === this.state.ticketId
    );

    // Get the selected site details
    const selectedSite = this.state.sites.find((s) => s.id == this.state.auctionSite);

    // Create ticket data for printing
    const ticketData = {
      // Basic ticket info
      ticket_number: this.state.ticketId,
      gatepass_number: this.state.gatepassNumber,
      weight_ticket: this.state.weightTicket,
      yard: {
        // <-- ADD THIS
        id: this.state.yardId,
        name: this.state.yard?.name || currentTicket?.yard?.name || 'Not Specified',
      },

      // Weight information
      tare_weight: parseFloat(this.state.tareWeight || 0),
      gross_weight: parseFloat(this.state.grossWeight || 0),
      net_weight: parseFloat(this.state.netWeight || 0),

      // Time information
      entry_time: this.state.actualArrival,
      exit_time: this.state.actualDeparture,

      // Vehicle information
      vehicle: {
        vehicle_number: this.state.vehicleNumber,
        vehicle_model: this.state.vehicleModel,
        trailer_number: this.state.trailerNumber,
      },

      // Driver information
      driver: {
        name: this.state.driverName,
        license_number: this.state.driverLicense,
        phone: this.state.driverContact,
      },

      // Delivery site information - crucial for printing
      delivery_site: currentTicket?.delivery_site || {
        id: this.state.auctionSite,
        project_name: selectedSite?.label || this.state.destination,
        auction_name: selectedSite?.name || '',

        // Material information
        material: {
          name:
            this.state.materialType ||
            currentTicket?.materialType ||
            selectedSite?.material?.name ||
            'No Material Specified',
        },

        // Location information
        load_from:
          selectedSite?.load_from ||
          this.state.loadingPoint ||
          currentTicket?.delivery_site?.load_from ||
          '',
        load_to:
          selectedSite?.load_to ||
          this.state.destination ||
          currentTicket?.delivery_site?.load_to ||
          '',

        // If you have buyer information in the site data
        buyer_name: selectedSite?.buyer_name || currentTicket?.delivery_site?.buyer_name || '',
        bos_number: selectedSite?.bos_number || currentTicket?.delivery_site?.bos_number || '',
      },

      // Material details for easy access
      materialType:
        this.state.materialType ||
        currentTicket?.materialType ||
        selectedSite?.material?.name ||
        'No Material Specified',
    };

    this.handlePrintTicket(ticketData, true);
  };

  render() {
    const {
      status,
      ticketId,
      operationType,
      vehicleNumber,
      trailerNumber,
      driverName,
      tareWeight,
      grossWeight,
      netWeight,
      errors,
      searchQuery,
      isEditing,
      isCompleted,
      loadingPoint,
      destination,
      sites,
      vehiclePhotos,
      auctionSite,
      vehicleId,
      vehicles,
      loadingSites,
      loadingVehicles,
      driverContact,
      gatepassNumber,
      weightTicket,
    } = this.state;
    const filteredTickets = this.getFilteredTickets();

    const shouldShowPrintComponent = this.state.printTicketData;

    return (
      <main className="main-content card material-loading">
        <div style={{ display: 'none', position: 'absolute', left: '-9999px' }}>
          {shouldShowPrintComponent && (
            <WeightTicketPrint
              ref={this.printRef}
              ticket={this.state.printTicketData}
              material={
                this.state.printMaterialName ||
                this.state.printTicketData?.materialName ||
                this.state.printTicketData?.delivery_site?.material?.name ||
                this.state.materialType ||
                ''
              }
              driver={{
                name: this.state.printTicketData?.driver?.name || this.state.driverName,
                license_number:
                  this.state.printTicketData?.driver?.license_number || this.state.driverLicense,
                phone: this.state.printTicketData?.driver?.phone || this.state.driverContact,
              }}
              yard={{
                // <-- ENSURE THIS IS PASSED CORRECTLY
                id: this.state.printTicketData?.yard?.id || this.state.yardId,
                name: this.state.printTicketData?.yard?.name || 'Not Specified',
              }}
              vehicle={{
                vehicle_number:
                  this.state.printTicketData?.vehicle?.vehicle_number || this.state.vehicleNumber,
                vehicle_model:
                  this.state.printTicketData?.vehicle?.vehicle_model || this.state.vehicleModel,
                trailer_number:
                  this.state.printTicketData?.vehicle?.trailer_number || this.state.trailerNumber,
              }}
              deliverySite={{
                auction_name:
                  this.state.printTicketData?.delivery_site?.auction_name ||
                  this.state.sites.find((s) => s.id == this.state.auctionSite)?.label,
                project_name:
                  this.state.printTicketData?.delivery_site?.project_name || this.state.destination,
                load_from: this.state.printTicketData?.delivery_site?.load_from,
                load_to: this.state.printTicketData?.delivery_site?.load_to,
                buyer_name: this.state.printTicketData?.delivery_site?.buyer_name,
                bos_number: this.state.printTicketData?.delivery_site?.bos_number,
              }}
            />
          )}
        </div>
        <div className="page-header">
          <button className="button1 button1-primary" onClick={() => window.history.back()}>
            <ArrowBigLeft /> {i18n.t('dashboard')}
          </button>

          <div className="page-title">
            <h1>{i18n.t('material_loading')}</h1>
          </div>

          <div className="header-actions">
            <button className="button1 button1-secondary" onClick={this.resetForm}>
              New Ticket
            </button>

            <button
              className="button1 button1-secondary"
              onClick={this.handleDirectDownload}
              disabled={
                this.state.isGeneratingPDF ||
                !this.state.ticketId ||
                this.state.ticketId.startsWith('TKT-')
              }
            >
              {this.state.isGeneratingPDF ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Generating...
                </>
              ) : (
                <>
                  <Download size={16} /> Download Ticket
                </>
              )}
            </button>

            <button
              className="button1 button1-primary"
              onClick={operationType === 'check-in' ? this.handleSubmit : this.handleExitSubmit}
              disabled={this.state.isLoading || this.state.isCompleted}
            >
              {this.state.isLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Processing...
                </>
              ) : operationType === 'check-in' ? (
                this.state.isCompleted ? (
                  'Ticket Completed'
                ) : (
                  'Create Entry Ticket'
                )
              ) : this.state.isCompleted ? (
                'Ticket Completed'
              ) : (
                'Record Exit'
              )}
            </button>
          </div>
        </div>

        <div className="content-grid">
          <div className="right-column">
            {/* <section className="card-t1">
              <div className="form-group full-width section-divider">
                <div className="card-t1-header">
                  <Package size={24} className="header-icon" />
                  <h2>Operation Summary</h2>
                </div>
              </div>


              <div className="operation-summary">
                <div className="summary-item">
                  <span className="label">Mode:</span>
                  <span className={`value ${isEditing ? 'editing' : 'creating'}`}>
                    {isEditing ? 'Editing' : 'Creating New'}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="label">Current Operation:</span>
                  <span className={`value ${operationType}`}>
                    {operationType === 'check-in' ? 'Vehicle Check-in' : 'Vehicle Check-out'}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="label">Vehicle:</span>
                  <span className="value">{vehicleNumber || 'Not specified'}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Driver:</span>
                  <span className="value">{driverName || 'Not specified'}</span>
                </div>
                {netWeight && (
                  <div className="summary-item">
                    <span className="label">Net Weight:</span>
                    <span className="value">{netWeight} kg</span>
                  </div>
                )}
              </div>
            </section> */}

            <section className="card-t1">
              <div className="form-group full-width section-divider">
                <div className="card-t1-header">
                  <Clock size={24} className="header-icon" />
                  <h2>
                    Recent Tickets
                    {isEditing && <span className="view-mode">(Click to Load)</span>}
                    <button
                      type="button"
                      className="button1 button1-primary"
                      onClick={this.fetchWeighbridgeTickets}
                      style={{ marginLeft: '10px', fontSize: '12px', padding: '2px 8px' }}
                    >
                      ↻ Refresh
                    </button>
                  </h2>
                </div>

                <div className="form-group">
                  <select
                    value={auctionSite}
                    onChange={this.handleSitesChange}
                    className={errors.auctionSite ? 'error' : ''}
                    disabled={loadingSites}
                  >
                    <option value="">Select Delivery Site</option>
                    {loadingSites ? (
                      <option value="" disabled>
                        Loading Sites...
                      </option>
                    ) : (
                      sites.map((site) => (
                        <option key={site.id} value={site.id}>
                          {site.label}
                        </option>
                      ))
                    )}
                  </select>
                  {errors.auctionSite && (
                    <span className="error-message">{errors.auctionSite}</span>
                  )}
                  <button
                    type="button"
                    className="button1 button1-primary"
                    onClick={this.fetchSitesList}
                    style={{
                      marginTop: '5px',
                      fontSize: '12px',
                      padding: '2px 8px',
                      textAlign: 'center',
                    }}
                  >
                    ↻ Refresh Sites
                  </button>
                </div>
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                  <button
                    type="button"
                    className={`button1 button1-${status === 'IN' ? 'primary' : 'gray'}`}
                    onClick={() => this.handleStatus('IN')}
                    disabled={this.state.isLoading}
                  >
                    IN
                  </button>
                  <button
                    type="button"
                    className={`button1 button1-${status === 'OUT' ? 'primary' : 'gray'}`}
                    onClick={() => this.handleStatus('OUT')}
                    disabled={this.state.isLoading}
                  >
                    OUT
                  </button>
                </div>
              </div>

              <div className="form-group">
                <div className="search-box">
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder="Search by vehicle, driver, or ticket..."
                    value={searchQuery}
                    onChange={(e) => this.handleSearch(e.target.value)}
                  />
                </div>
              </div>

              {this.state.isLoadingTickets ? (
                <div className="loading-container">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span>Loading tickets...</span>
                </div>
              ) : (
                <div className="transactions-list-container">
                  <div className="transactions-list">
                    {filteredTickets.map((ticket) => (
                      <div
                        key={ticket.id}
                        className={`transaction-item ${isEditing && ticket.ticketNumber === ticketId ? 'active' : ''}`}
                        onClick={() => this.handleTicketClick(ticket)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="transaction-header">
                          <span className="vehicle-number">
                            {ticket.vehicleNumber}
                            <strong className="transaction-details">
                              ( {ticket.ticketNumber} )
                            </strong>
                          </span>
                          <span className={`status-badge ${ticket.status}`}>{ticket.status}</span>
                        </div>
                        <div className="transaction-details">
                          <div>{ticket.delivery_site?.auction_name}</div>
                          <div className="ticket-meta">
                            <small>
                              {ticket.checkInTime
                                ? new Date(ticket.checkInTime).toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : ''}
                              {ticket.tareWeight &&
                                ` • Tare: ${parseFloat(ticket.tareWeight).toLocaleString()} kg`}
                            </small>
                          </div>
                        </div>
                        <div className="transaction-hover">Click to load this ticket</div>
                      </div>
                    ))}
                    {filteredTickets.length === 0 && (
                      <div className="no-results">No tickets found</div>
                    )}
                  </div>
                </div>
              )}
            </section>
          </div>

          <section className="card-t1 vehicle-details">
            <div className="form-grid">
              <div className="form-group full-width ">
                <div className="section-header section-divider">
                  <div className="card-t1-header">
                    <Truck size={24} className="header-icon" />
                    <h2>
                      {i18n.t('load_information')}
                      {vehicleNumber && ` - (${vehicleNumber})`}
                    </h2>
                  </div>
                </div>
              </div>

              {operationType === 'check-in' && (
                <>
                  <div className="form-group" style={{ width: '220px' }}>
                    <label className="required">{i18n.t('auction_sites')}</label>
                    <select
                      value={auctionSite}
                      onChange={this.handleSitesChange}
                      className={errors.auctionSite ? 'error' : ''}
                      disabled={loadingSites}
                    >
                      <option value="">Select Delivery Site</option>
                      {loadingSites ? (
                        <option value="" disabled>
                          Loading Sites...
                        </option>
                      ) : (
                        sites.map((site) => (
                          <option key={site.id} value={site.id}>
                            {site.label}
                          </option>
                        ))
                      )}
                    </select>
                    {errors.auctionSite && (
                      <span className="error-message">{errors.auctionSite}</span>
                    )}
                    <button
                      type="button"
                      className="button1 button1-primary"
                      onClick={this.fetchSitesList}
                      style={{
                        marginTop: '5px',
                        fontSize: '12px',
                        padding: '2px 8px',
                        textAlign: 'center',
                      }}
                    >
                      ↻ Refresh Sites
                    </button>
                  </div>
                </>
              )}

              {operationType === 'check-in' && auctionSite && (
                <div className="form-group" style={{ width: '220px' }}>
                  <label>{i18n.t('vehicle_number')}</label>
                  <select
                    value={vehicleId}
                    onChange={this.handleVehicleChange}
                    className="driver-search"
                    disabled={loadingVehicles || vehicles.length === 0}
                  >
                    <option value="">Select Vehicle</option>
                    {loadingVehicles ? (
                      <option value="" disabled>
                        Loading Vehicles...
                      </option>
                    ) : vehicles.length === 0 ? (
                      <option value="" disabled>
                        No vehicles assigned to this site
                      </option>
                    ) : (
                      vehicles.map((vehicle) => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.vehicle_number}
                          {vehicle.vehicle_model ? ` - ${vehicle.vehicle_model}` : ''}
                          {vehicle.driver?.name ? ` (Driver: ${vehicle.driver.name})` : ''}
                        </option>
                      ))
                    )}
                  </select>
                  <button
                    type="button"
                    className="button1 button1-primary"
                    onClick={() => auctionSite && this.fetchVehiclesList(auctionSite)}
                    style={{
                      marginTop: '5px',
                      fontSize: '12px',
                      padding: '2px 8px',
                      textAlign: 'center',
                    }}
                    disabled={!auctionSite}
                  >
                    ↻ Refresh Vehicles
                  </button>
                </div>
              )}

              {operationType === 'check-out' &&
                this.state.currentTicketYards &&
                this.state.currentTicketYards.length > 0 && (
                  <div className="form-group">
                    <label className="required">Select Yard</label>
                    <select
                      value={this.state.yardId}
                      onChange={(e) =>
                        this.setState({
                          yardId: e.target.value,
                          errors: { ...this.state.errors, yardId: '' },
                        })
                      }
                      className={this.state.errors.yardId ? 'error' : ''}
                    >
                      <option value="">Select a Yard</option>
                      {this.state.currentTicketYards.map((yard) => (
                        <option key={yard.id} value={yard.id}>
                          {yard.name} - Loaded: {yard.loaded_weight} kg
                        </option>
                      ))}
                    </select>
                    {this.state.errors.yardId && (
                      <span className="error-message">{this.state.errors.yardId}</span>
                    )}
                    <small className="text-muted">
                      This delivery site has {this.state.currentTicketYards.length} yard(s)
                    </small>
                  </div>
                )}

              {operationType && (
                <div className="form-group">
                  <label className="required">{i18n.t('tare_weight_kg')}</label>
                  <input
                    type="number"
                    placeholder="Empty vehicle weight"
                    value={tareWeight}
                    onChange={(e) => this.handleInputChange('tareWeight', e.target.value)}
                    className={errors.tareWeight ? 'error' : ''}
                    min="0"
                    step="0.01"
                    readOnly={operationType === 'check-out'}
                  />
                  {errors.tareWeight && <span className="error-message">{errors.tareWeight}</span>}
                </div>
              )}

              {operationType === 'check-in' && (
                <>
                  <div className="form-group">
                    <label className="required">Load From</label>
                    <input
                      type="text"
                      placeholder="Loading location"
                      value={loadingPoint}
                      onChange={(e) => this.handleInputChange('loadingPoint', e.target.value)}
                      className={auctionSite ? 'readonly-input' : ''}
                      readOnly={!!auctionSite}
                    />
                    {errors.loadingPoint && (
                      <span className="error-message">{errors.loadingPoint}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="required">Load To</label>
                    <input
                      type="text"
                      placeholder="Destination"
                      value={destination}
                      onChange={(e) => this.handleInputChange('destination', e.target.value)}
                      className={auctionSite ? 'readonly-input' : ''}
                      readOnly={!!auctionSite}
                    />
                    {errors.destination && (
                      <span className="error-message">{errors.destination}</span>
                    )}
                  </div>
                </>
              )}

              {vehicleId && (
                <>
                  <div className="form-group">
                    <label className="required">{i18n.t('driver_name')}</label>
                    <input
                      type="text"
                      placeholder="Driver full name"
                      value={driverName}
                      onChange={(e) => this.handleInputChange('driverName', e.target.value)}
                      className={driverName ? 'readonly-input' : ''}
                      readOnly={!!driverName}
                    />
                    {errors.driverName && (
                      <span className="error-message">{errors.driverName}</span>
                    )}
                    {!driverName && (
                      <small className="text-muted">
                        No driver assigned. Please enter driver name.
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="required">{i18n.t('driver_number')}</label>
                    <input
                      type="text"
                      placeholder="Driver phone number"
                      value={driverContact}
                      onChange={(e) => this.handleInputChange('driverContact', e.target.value)}
                      className={driverContact ? 'readonly-input' : ''}
                      readOnly={!!driverContact}
                    />
                    {errors.driverContact && (
                      <span className="error-message">{errors.driverContact}</span>
                    )}
                    {!driverContact && (
                      <small className="text-muted">
                        No driver contact. Please enter phone number.
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label>{i18n.t('driver_license')}</label>
                    <input
                      type="text"
                      placeholder="Driver license number"
                      value={this.state.driverLicense}
                      onChange={(e) => this.handleInputChange('driverLicense', e.target.value)}
                      className={this.state.driverLicense ? 'readonly-input' : ''}
                      readOnly={!!this.state.driverLicense}
                    />
                  </div>
                </>
              )}

              {operationType === 'check-out' && (
                <>
                  <div className="form-group">
                    <label className="required">Gross Weight (Kg)</label>
                    <input
                      type="number"
                      placeholder="Loaded vehicle weight"
                      value={grossWeight}
                      onChange={(e) => this.handleInputChange('grossWeight', e.target.value)}
                      className={errors.grossWeight ? 'error' : ''}
                      min="0"
                      step="0.01"
                      readOnly={this.state.isCompleted}
                      disabled={this.state.isCompleted}
                    />
                    {errors.grossWeight && (
                      <span className="error-message">{errors.grossWeight}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="required">Net Weight (Kg)</label>
                    <input
                      type="number"
                      placeholder="Calculated net weight"
                      value={
                        tareWeight &&
                        grossWeight &&
                        parseFloat(tareWeight) > 0 &&
                        parseFloat(grossWeight) > 0
                          ? (parseFloat(grossWeight) - parseFloat(tareWeight)).toFixed(2)
                          : ''
                      }
                      readOnly
                      className="readonly-input"
                    />
                    {errors.netWeight && <span className="error-message">{errors.netWeight}</span>}
                  </div>

                  <div className="form-group">
                    <label className="required">Gatepass Number</label>
                    <input
                      type="text"
                      placeholder="Enter gatepass number"
                      value={gatepassNumber}
                      onChange={(e) => this.handleInputChange('gatepassNumber', e.target.value)}
                      className={errors.gatepassNumber ? 'error' : ''}
                      readOnly={isCompleted}
                      disabled={isCompleted}
                    />
                    {errors.gatepassNumber && (
                      <span className="error-message">{errors.gatepassNumber}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="required">Weight Ticket</label>
                    <input
                      type="text"
                      placeholder="Enter weight ticket number"
                      value={weightTicket}
                      onChange={(e) => this.handleInputChange('weightTicket', e.target.value)}
                      readOnly={isCompleted}
                      disabled={isCompleted}
                    />
                  </div>
                </>
              )}

              {operationType === 'check-out' && (
                <div className="form-group full-width">
                  <label className="required">Vehicle Photos (Required for Check-out)</label>
                  <div className="photo-grid">
                    {vehiclePhotos.map((photo) => (
                      <div key={photo.id} className="photo-item">
                        <img
                          src={photo.url}
                          alt={`Vehicle ${vehicleNumber}`}
                          onError={(e) => {
                            console.error('Error loading image:', photo.url);
                            e.target.src = 'https://via.placeholder.com/150?text=Image+Not+Found';
                          }}
                        />
                        {!isCompleted && (
                          <button
                            className="remove-photo"
                            onClick={() => this.removePhoto(photo.id)}
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                    {!isCompleted && (
                      <div className="photo-upload">
                        <Upload size={32} />
                        <span>Add Photos (Required for Check-out)</span>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={this.handlePhotoUpload}
                          className="photo-upload-input"
                        />
                      </div>
                    )}
                  </div>
                  {!isCompleted && errors.photos && (
                    <span className="error-message">{errors.photos}</span>
                  )}
                  <small className="text-muted">
                    Max 5 photos, each max 5MB. At least one required for check-out.
                  </small>
                </div>
              )}
            </div>
          </section>

          {this.state.showPrintModal && (
            <div className="modal-overlay">
              <div className="modal-content print-modal">
                <div className="modal-header">
                  <h3>
                    <Printer size={20} /> Weight Ticket Preview -{' '}
                    {this.state.printTicketData?.ticket_number}
                  </h3>
                  <button onClick={() => this.setState({ showPrintModal: false })}>×</button>
                </div>
                <div className="modal-body">
                  <div className="print-preview-container">
                    <WeightTicketPrint
                      ref={this.printRef}
                      ticket={this.state.printTicketData}
                      driver={this.state.printDriverInfo}
                      vehicle={this.state.printVehicleInfo}
                      deliverySite={this.state.printDeliverySiteInfo}
                    />
                  </div>

                  <div className="print-actions">
                    <button
                      className="button1 button1-primary"
                      onClick={() => this.generateAndDownloadPDF(true)}
                      disabled={this.state.isGeneratingPDF}
                    >
                      {this.state.isGeneratingPDF ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Downloading...
                        </>
                      ) : (
                        <>
                          <Download size={16} /> Download PDF
                        </>
                      )}
                    </button>

                    <button className="button1 button1-secondary" onClick={() => window.print()}>
                      <Printer size={16} /> Print
                    </button>

                    <button
                      className="button1 button1-outline"
                      onClick={() => this.setState({ showPrintModal: false })}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {this.state.showSuccessModal && this.state.createdTicket && (
            <div className="modal-overlay">
              <div className="modal-content success-modal">
                <div className="modal-header">
                  <h3>✓ Ticket Created Successfully!</h3>
                  <button onClick={() => this.setState({ showSuccessModal: false })}>×</button>
                </div>
                <div className="modal-body">
                  <div className="ticket-summary">
                    <div className="ticket-header">
                      <h4>Ticket Details:</h4>
                      <div className="ticket-number">
                        <strong>Ticket #:</strong> {this.state.createdTicket.ticket_number}
                      </div>
                    </div>

                    <div className="ticket-info-grid">
                      <div className="info-item">
                        <span className="label">Vehicle:</span>
                        <span className="value">
                          {this.state.createdTicket.vehicle?.vehicle_number} -{' '}
                          {this.state.createdTicket.vehicle?.vehicle_model}
                        </span>
                      </div>

                      <div className="info-item">
                        <span className="label">Tare Weight:</span>
                        <span className="value">{this.state.createdTicket.tare_weight} kg</span>
                      </div>

                      <div className="info-item">
                        <span className="label">Entry Time:</span>
                        <span className="value">
                          {new Date(this.state.createdTicket.entry_time).toLocaleString()}
                        </span>
                      </div>

                      <div className="info-item">
                        <span className="label">Delivery Site:</span>
                        <span className="value">
                          {this.state.createdTicket.delivery_site?.project_name}
                        </span>
                      </div>

                      <div className="info-item">
                        <span className="label">Status:</span>
                        <span className="value status-badge active">
                          {this.state.createdTicket.status}
                        </span>
                      </div>
                    </div>

                    <div className="modal-actions">
                      <button
                        className="button1 button1-primary"
                        onClick={() => this.handlePrintTicket(this.state.createdTicket)}
                        disabled={this.state.isGeneratingPDF}
                      >
                        {this.state.isGeneratingPDF ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Generating...
                          </>
                        ) : (
                          <>
                            <Download size={16} /> Download Ticket
                          </>
                        )}
                      </button>
                      <button
                        className="button1 button1-secondary"
                        onClick={() => this.setState({ showSuccessModal: false })}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div
            className="auto-download-setting"
            style={{
              position: 'fixed',
              bottom: '20px',
              right: '20px',
              background: 'white',
              padding: '10px',
              borderRadius: '5px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              zIndex: 1000,
            }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                checked={this.state.autoDownloadAfterExit}
                onChange={(e) => this.setState({ autoDownloadAfterExit: e.target.checked })}
              />
              <span>Auto-download weight ticket after exit</span>
            </label>
          </div>
        </div>
      </main>
    );
  }
}

export default MaterialLoading;
